/**
 * Ring denominator (the value that maps to a full 360°). This is intentionally
 * decoupled from the number shown in the ring centre.
 *
 * iOS DeviceActivity reports a deduplicated `totalScreenTimeSeconds` (Apple's
 * `totalActivityDuration`) that can be smaller than the sum of the per-app
 * durations it also reports, because concurrent/overlapping attributions are
 * double-counted per app but collapsed in the total. Using that smaller total
 * as the denominator let the displayed arcs sum past the circumference, so the
 * ring overfilled (full 360° with thin slivers of overlapping arcs instead of a
 * dark remainder track).
 *
 * Taking the largest of (reported total, full app-population total, displayed
 * segment sum) guarantees the arcs can never exceed 360° and that the dark
 * remainder always represents "all other apps". On normal days the reported
 * total is the largest term, so this is a no-op.
 */
export function resolveRingDenominator(
  reportedTotal: number,
  populationTotal: number,
  segmentsSum: number,
): number {
  return Math.max(reportedTotal, populationTotal, segmentsSum, 0);
}

/** Dash length + clockwise offset for each arc, in stroke-dash units. */
export function computeRingSegments(
  values: number[],
  denominator: number,
  circumference: number,
): Array<{ dash: number; gapOffset: number }> {
  const segmentData: Array<{ dash: number; gapOffset: number }> = [];
  let runningOffset = 0;
  for (const value of values) {
    const ratio = denominator > 0 ? value / denominator : 0;
    const dash = Math.max(0, ratio * circumference - 2);
    segmentData.push({ dash, gapOffset: runningOffset });
    runningOffset += ratio * circumference;
  }
  return segmentData;
}
